/* Which product ingestion schedule key the chosen mode makes inapplicable.

   product_ingestion_schedule_mode says which of the two value keys DECIDES the
   schedule. Showing a filled field that does not decide anything misleads more
   than the empty field it replaced, which is why RPAA-265 treats the key and
   this filtering as one change rather than two.

   THE TWO KEYS ARE NOT SYMMETRIC, and an earlier version of this comment said
   they were ("the other one is stored and inert"), which is the sentence a
   future reader would use to decide whether hiding is safe. Corrected by
   independent review, 09-Set-2026 (m1):

     - product_ingestion_daily_times under "interval" mode really is inert.
       build_ingestion_trigger returns the IntervalTrigger before it parses the
       times, so even an unparseable value there changes nothing.
     - product_ingestion_interval_minutes under "daily_times" mode is NOT.
       _add_scheduled_job validates it on every call, BEFORE the mode is read at
       all, so 0, a negative or a non-numeric value refuses the cron and costs
       the tenant its schedule. The backend states this in
       add_product_ingestion_job's Args block and the spec calls the asymmetry
       out by name.

   That asymmetry is why hiding needs a caption and not only a filter.
   INAPPLICABLE_SCHEDULE_REASON_KEYS in ConfigurationsPage renders one, and the
   interval's copy says the value is still validated. Without it, a stored 0
   plus a switch to "daily_times" leaves an operator with a 200, no job after
   the next restart, and no field on the page to read or correct.

   Client-side rather than driven by the response envelope, and the reason is
   measured: `visibility` is computed from the STORED workflow, so an
   envelope-driven filter would keep the newly-relevant field hidden until a
   save landed, making "switch mode and type the times" two saves.

   The keys are only left out of the RENDER. They stay in formValues and so in
   what the page saves, because update_config replaces the whole settings JSONB
   and a dropped key would be deleted from the row. This is the same rule
   renderBoolMapBox states for hidden tool toggles.

   A NON-EMPTY unrecognised mode hides nothing. The backend refuses such a
   value, so nothing is scheduled, and the operator needs both fields visible
   to see why; guessing which one applies would hide the evidence. That is the
   rationale for "cron" and for any other typo.

   An EMPTY or WHITESPACE-ONLY mode is a different case and takes the INTERVAL
   on both sides. The backend's normalize_schedule_mode guards twice,
   `str(m or INTERVAL_MODE).strip().lower() or INTERVAL_MODE`: "" is caught by
   the leading `or` and "   " by the trailing one. This helper carries both
   guards below for that reason. Read whitespace-only as a refusal and the page
   hides nothing while the backend runs the interval, which shows a filled,
   visible daily_times for a tenant running hourly, the exact state RPAA-265
   exists to remove. It is reachable, because the mode renders as free text and
   handleSave does not trim. */
const INTERVAL_KEY = "product_ingestion_interval_minutes";
const DAILY_TIMES_KEY = "product_ingestion_daily_times";

export function inapplicableScheduleKeys(scheduleMode) {
  /* Anything that is not a string hides NOTHING, and this one guard replaces
     what used to be a per-shape enumeration that got the shapes wrong.

     undefined and null mean the backend predates the key: hiding the times
     field when no mode field exists to reveal it again would delete a working
     control from the page. Every OTHER non-string is unstorable through the
     API, because validate_assignment on WorkflowConfig rejects a non-string for
     a str field, so its only origin is a direct jsonb write — an anomaly whose
     operator needs both fields visible, which is the same arm a typo takes.

     Why a type test and not a value-by-value match. String() and Python's str()
     disagree about arrays, and the old comment claimed {} was "the single value
     the two languages read differently". It was not: measured 09-Set-2026,
     String(["daily_times"]) is "daily_times" while str(["daily_times"]) is
     "['daily_times']", so ["daily_times"], ["interval"], [""] and
     [["daily_times"]] all diverged too — and unlike {} they landed on the
     UNSAFE arm, hiding a field while the backend refused the mode outright and
     scheduled nothing. Found by independent review (m2). A type test makes the
     whole class conservative at once instead of enumerating it, and there is
     nothing left to keep in step with Python's str().

     An empty string is NOT this case: the key exists and an operator cleared
     it, so it follows the backend's default. */
  if (typeof scheduleMode !== "string") return [];
  const mode = String(scheduleMode || "interval").trim().toLowerCase() || "interval";
  if (mode === "interval") return [DAILY_TIMES_KEY];
  if (mode === "daily_times") return [INTERVAL_KEY];
  return [];
}
